import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import PaymentSetupForm from '../../components/PaymentSetupForm';
import billingApi from '../../services/billingApi';
import {
  resolveStripePublishableKey,
  getStripeKeyMode,
  stripeTestModeErrorMessage,
  isProductionAppHost,
} from '../../config/stripe';
import { formatPricePerTransaction } from '../../config/billing';
import { applyBillingProfile } from '../../utils/billingRegion';
import { getBillingReturnUrl } from '../../utils/paymentReturnUrl';
import { getSetupIntentIdFromUrlSearchParams } from '../../utils/stripeSetupIntent';
import { savePendingSetupIntent } from '../../utils/pendingSetupIntent';
import { SupportContact } from '../../components/ui/SupportContact';
import { useAuth } from '../../auth/useAuth';

function formatMoney(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format((cents || 0) / 100);
}

function formatInvoiceDate(unixSeconds) {
  if (!unixSeconds) return '—';
  return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCardBrand(brand) {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function invoiceStatusClass(status) {
  if (status === 'paid') return 'provider-badge--completed';
  if (status === 'open') return 'provider-badge--pending';
  if (status === 'failed' || status === 'uncollectible') return 'provider-badge--failed';
  return 'provider-badge--default';
}

const ProviderBilling = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [account, setAccount] = useState(null);
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [publishableKey, setPublishableKey] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [returnUrl, setReturnUrl] = useState('');
  const [updatingCard, setUpdatingCard] = useState(false);
  const completingRedirectRef = useRef(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/provider/login');
  };

  const loadAccount = async (accessToken) => {
    const data = await billingApi.getAccount(accessToken);
    applyBillingProfile(data);
    setAccount(data);
    return data;
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/provider/login');
      return;
    }

    const init = async () => {
      try {
        setLoading(true);
        setError('');
        setSuccess('');

        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          navigate('/provider/login');
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const returnedSetupIntentId = getSetupIntentIdFromUrlSearchParams(params);
        const redirectStatus = params.get('redirect_status');
        const returnedClientSecret = params.get('setup_intent_client_secret');

        if (
          returnedSetupIntentId &&
          (redirectStatus === 'succeeded' || (returnedClientSecret && !redirectStatus))
        ) {
          if (!completingRedirectRef.current) {
            completingRedirectRef.current = true;
            await billingApi.completeSetup(
              accessToken,
              returnedSetupIntentId,
              returnedClientSecret,
              { updateOnly: true }
            );
            setSuccess('Payment method updated.');
          }
          window.history.replaceState({}, '', '/provider/billing');
        } else if (returnedSetupIntentId && redirectStatus && redirectStatus !== 'succeeded') {
          setError('Card update was not completed. Please try again.');
          window.history.replaceState({}, '', '/provider/billing');
        }

        await loadAccount(accessToken);
      } catch (err) {
        setError(err.message || 'Unable to load billing.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleStartUpdateCard = async () => {
    try {
      setError('');
      setSuccess('');
      setUpdatingCard(true);

      const accessToken = localStorage.getItem('accessToken');
      const setup = await billingApi.createSetupIntent(accessToken);
      const stripeKey = setup.stripePublishableKey || (await resolveStripePublishableKey());

      if (!stripeKey || !setup.clientSecret) {
        throw new Error('Could not start card update.');
      }

      if (isProductionAppHost() && getStripeKeyMode(stripeKey) === 'test') {
        throw new Error(stripeTestModeErrorMessage());
      }

      const intentId =
        setup.setupIntentId ||
        (setup.clientSecret ? setup.clientSecret.split('_secret_')[0] : '');
      savePendingSetupIntent(intentId, setup.clientSecret);

      setPublishableKey(stripeKey);
      setReturnUrl(setup.billingReturnUrl || getBillingReturnUrl());
      setClientSecret(setup.clientSecret);
      setShowUpdateCard(true);
    } catch (err) {
      setError(err.message || 'Could not start card update.');
      setShowUpdateCard(false);
    } finally {
      setUpdatingCard(false);
    }
  };

  const handleCardUpdated = async () => {
    setShowUpdateCard(false);
    setClientSecret('');
    setSuccess('Payment method updated.');
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) await loadAccount(accessToken);
  };

  const billingEmail = user?.attributes?.email || account?.email || '';
  const billingName = [user?.attributes?.given_name, user?.attributes?.family_name]
    .filter(Boolean)
    .join(' ');

  if (authLoading || loading) {
    return (
      <ProviderPageLayout variant="app" wide onSignOut={handleSignOut}>
        <div className="provider-loading">
          <div className="provider-spinner" />
          <span>Loading billing…</span>
        </div>
      </ProviderPageLayout>
    );
  }

  return (
    <ProviderPageLayout variant="app" wide onSignOut={handleSignOut}>
      <div className="provider-page-header">
        <div>
          <h1>Billing</h1>
          <p className="provider-page-meta">
            {formatPricePerTransaction({
              currency: account?.currency,
              pricePerTransactionDollars: account?.pricePerTransactionDollars,
              countryCode: account?.countryCode,
            })}{' '}
            per transaction
            {account?.regionLabel ? ` · ${account.regionLabel}` : ''}
          </p>
        </div>
        <div className="provider-billing-header-actions">
          <SupportContact
            email={billingEmail}
            storeName={user?.attributes?.['custom:store_name'] || ''}
            storeCode={user?.attributes?.['custom:device_id'] || ''}
            triggerLabel="Support"
            triggerClassName="provider-btn provider-btn--ghost provider-btn--sm provider-btn--inline"
          />
          <Link to="/provider/" className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline">
            ← Dashboard
          </Link>
        </div>
      </div>

      {account?.testClient && (
        <div className="provider-alert provider-alert--info">
          <strong>Test account</strong> — automated monthly invoices are not created.
        </div>
      )}

      {error && <div className="provider-alert provider-alert--error">{error}</div>}
      {success && <div className="provider-alert provider-alert--success">{success}</div>}

      <div className="provider-panel">
        <h2 className="provider-panel-title">Payment method</h2>
        {account?.paymentMethod ? (
          <>
            <div className="provider-payment-card">
              <div className="provider-payment-card-chip" aria-hidden="true" />
              <div className="provider-payment-card-body">
                <p className="provider-payment-card-brand">
                  {formatCardBrand(account.paymentMethod.brand)}
                </p>
                <p className="provider-payment-card-number">
                  ···· ···· ···· {account.paymentMethod.last4}
                </p>
                <p className="provider-payment-card-meta">
                  Expires {account.paymentMethod.expMonth}/{account.paymentMethod.expYear}
                  {billingEmail ? ` · ${billingEmail}` : ''}
                </p>
              </div>
            </div>

            {!showUpdateCard && (
              <div className="provider-payment-actions">
                <button
                  type="button"
                  onClick={handleStartUpdateCard}
                  disabled={updatingCard}
                  className="provider-btn provider-btn--secondary provider-btn--sm provider-btn--inline"
                >
                  {updatingCard ? 'Loading…' : 'Update card'}
                </button>
              </div>
            )}
            <p className="provider-field-help" style={{ marginTop: 10 }}>
              A payment method must stay on file while your device is active so accrued usage can
              be billed. To replace it, use <strong>Update card</strong> — the new card takes over
              seamlessly.
            </p>
          </>
        ) : (
          <p style={{ color: 'var(--color-text-muted)', marginBottom: 16 }}>No payment method on file.</p>
        )}

        {!account?.paymentMethod && !showUpdateCard && (
          <button
            type="button"
            onClick={handleStartUpdateCard}
            disabled={updatingCard}
            className="provider-btn provider-btn--primary provider-btn--inline"
          >
            {updatingCard ? 'Loading…' : 'Add card'}
          </button>
        )}

        {showUpdateCard && publishableKey && clientSecret && (
          <div style={{ marginTop: 20 }}>
            <PaymentSetupForm
              publishableKey={publishableKey}
              clientSecret={clientSecret}
              returnUrl={returnUrl}
              billingEmail={billingEmail}
              billingName={billingName}
              submitLabel="Save new card"
              updateOnly
              onSuccess={handleCardUpdated}
            />
            <button
              type="button"
              className="provider-link"
              style={{ marginTop: 12 }}
              onClick={() => {
                setShowUpdateCard(false);
                setClientSecret('');
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="provider-panel">
        <h2 className="provider-panel-title">Invoices</h2>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
          Usage is invoiced on the <strong>2nd of each month</strong> at 04:45 UTC for the previous
          calendar month.
        </p>

        {account?.invoices?.length > 0 ? (
          <div className="provider-table-wrap">
            <table className="provider-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Invoice</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {account.invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{formatInvoiceDate(inv.created)}</td>
                    <td>{inv.billingPeriod || '—'}</td>
                    <td>{inv.number || inv.id}</td>
                    <td>{formatMoney(inv.total, inv.currency)}</td>
                    <td>
                      <span className={`provider-badge ${invoiceStatusClass(inv.status)}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td>
                      {inv.hostedInvoiceUrl && (
                        <a
                          href={inv.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="provider-link"
                          style={{ fontSize: 13, marginRight: 12 }}
                        >
                          View
                        </a>
                      )}
                      {inv.invoicePdf && (
                        <a
                          href={inv.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="provider-link"
                          style={{ fontSize: 13 }}
                        >
                          PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="provider-empty" style={{ padding: '24px 0' }}>
            No invoices yet. They will appear here after monthly billing runs.
          </p>
        )}
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderBilling;
